import { useContext } from 'react';

import { createContext, getWithContextHOC, Logger } from '@futrli/shared-services';

import { userGridSettingsPersistor } from './user-grid-settings-persistor';
import { UserGridSettingsContextType, UserGridSettingsContextKeys } from './types';

const storage = userGridSettingsPersistor.instance;

export const UserGridSettingsContext = createContext<UserGridSettingsContextType>({
  seeded: storage.seeded,
  setSeeded: () => {
    Logger.warn('function [setSeeded] not implemented');
  },
  rowHeights: storage.rowHeights,
  setRowHeights: () => {
    Logger.warn('function [setRowHeight] not implemented');
  },
  showDecimals: storage.showDecimals,
  setShowDecimals: () => {
    Logger.warn('function [setShowDecimals] not implemented');
  },
  getShowZeroValueAccounts: () => {
    Logger.warn('function [getShowZeroValueAccounts] not implemented');
    return false;
  },
  setShowZeroValueAccounts: () => {
    Logger.warn('function [setShowZeroValueAccounts] not implemented');
  },
  showZeroValueAccountsMap: storage.showZeroValueAccountsMap,
  expandedRows: storage.expandedRows,
  toggleExpandedRows: () => {
    Logger.warn('function [toggleExpandedRows] not implemented');
  },
  clearExpandedRows: () => {
    Logger.warn('function [clearExpandedRows] not implemented');
  },
  clearEditingRows: () => {
    Logger.warn('function [clearEditingRows] not implemented');
  },
  activateEditingMode: () => {
    Logger.warn('function [activateEditingMode] not implemented');
  },
  deactivateEditingMode: () => {
    Logger.warn('function [deactivateEditingMode] not implemented');
  },
  isInEditingMode: () => false,
  updateEditingCell: () => {
    Logger.warn('function [updateEditingCell] not implemented');
  },
  getEditingRowCellValues: () => {
    Logger.warn('function [getEditingRowCellValues] not implemented');
    return undefined;
  },
  bulkUpdatesInvoicesBills: {
    selectedInvoicesBills: [],
    isCustomSelect: false,
    showInvoicesBillsCheckboxes: false,
    filters: undefined,
    triggerInvoicesBillsRefetch: 0,
    handleChangeSelectedInvoicesBills: () => {
      Logger.warn('function [handleChangeSelectedInvoicesBills] not implemented');
    },
    handleChangeIsCustomSelect: () => {
      Logger.warn('function [handleChangeIsCustomSelect] not implemented');
    },
    handleChangeShowCheckboxes: () => {
      Logger.warn('function [handleChangeShowCheckboxes] not implemented');
    },
    handleFiltersChange: () => {
      Logger.warn('function [handleFiltersChange] not implemented');
    },
    handleTriggerInvoicesBillsRefetch: () => {
      Logger.warn('function [handleTriggerInvoicesBillsRefetch] not implemented');
    }
  }
});

const DEFAULT_OPTIONS: UserGridSettingsContextKeys[] = [
  'seeded',
  'setSeeded',
  'rowHeights',
  'setRowHeights',
  'showDecimals',
  'setShowDecimals',
  'getShowZeroValueAccounts',
  'setShowZeroValueAccounts',
  'expandedRows',
  'toggleExpandedRows',
  'clearExpandedRows',
  'clearEditingRows',
  'activateEditingMode',
  'deactivateEditingMode',
  'isInEditingMode',
  'updateEditingCell',
  'getEditingRowCellValues'
];

/**
 * Injects the context to the component passed as param.
 * @name withUserGridSettingsContext
 * @function
 * @param {React.ComponentType<RC & CP>} Component - Component to wrap with the consumer.
 * @param {Array<keyof C>} options - Context props to inject into the component.
 * @template {RC} - React component props without context props.
 * @template {CP} - Context props of the component.
 *
 * @returns {React.ComponentType<RC>} - Wrapped component.
 */
export const withUserGridSettingsContext = getWithContextHOC(UserGridSettingsContext, DEFAULT_OPTIONS);

export const useUserGridSettingsContext = () => {
  return useContext(UserGridSettingsContext);
};
